import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { NARRATIONS } from '../config'

const NarrationCtx = createContext(null)

/**
 * Owns the single <audio> element + WebAudio graph for the whole site.
 * One global play/stop control drives whichever section is on screen.
 * - play(sectionId): crossfade into that section's narration
 * - stop(): INSTANT stop (user pressed stop — must always work)
 * - a token system invalidates stale async starts, so rapid clicks can
 *   never leave the state machine inconsistent
 * - manual stop only suppresses auto-play for THAT section until it is left
 * The analyser output drives the floating character's lip-sync.
 */
export function NarrationProvider({ children }) {
  const audioRef = useRef(null)
  const audioCtxRef = useRef(null)
  const analyserRef = useRef(null)
  const gainRef = useRef(null)
  const dataRef = useRef(new Uint8Array(128))
  const playingRef = useRef(false)
  const tokenRef = useRef(0)
  const suppressAutoForRef = useRef(null) // section where the user hit stop
  const endedForRef = useRef(null) // last section whose narration ended naturally
  const gestureRef = useRef(false)
  const [listened, setListened] = useState([])
  const [current, setCurrent] = useState(null)
  const [playing, setPlaying] = useState(false)

  const getAudio = useCallback(() => {
    if (!audioRef.current) {
      const a = new Audio()
      a.preload = 'none'
      audioRef.current = a
    }
    return audioRef.current
  }, [])

  const ensureGraph = useCallback(() => {
    const a = getAudio()
    if (audioCtxRef.current) return
    try {
      const AC = window.AudioContext || window.webkitAudioContext
      const ctx = new AC()
      const src = ctx.createMediaElementSource(a)
      const analyser = ctx.createAnalyser()
      analyser.fftSize = 256
      analyser.smoothingTimeConstant = 0.7
      const gain = ctx.createGain()
      src.connect(analyser)
      analyser.connect(gain)
      gain.connect(ctx.destination)
      audioCtxRef.current = ctx
      analyserRef.current = analyser
      gainRef.current = gain
    } catch (err) {
      // no WebAudio — volume ramps fall back to element.volume
    }
  }, [getAudio])

  const ramp = useCallback(
    (to, ms) => {
      const ctx = audioCtxRef.current
      const gain = gainRef.current
      const a = getAudio()
      if (ctx && gain) {
        const now = ctx.currentTime
        gain.gain.cancelScheduledValues(now)
        gain.gain.setValueAtTime(gain.gain.value, now)
        gain.gain.linearRampToValueAtTime(to, now + ms / 1000)
      } else {
        const from = a.volume
        const start = performance.now()
        const step = (t) => {
          const p = ms > 0 ? Math.min(1, (t - start) / ms) : 1
          a.volume = from + (to - from) * p
          if (p < 1) requestAnimationFrame(step)
        }
        requestAnimationFrame(step)
      }
    },
    [getAudio]
  )

  useEffect(() => {
    const onGesture = () => {
      gestureRef.current = true
    }
    window.addEventListener('pointerdown', onGesture)
    window.addEventListener('keydown', onGesture)
    return () => {
      window.removeEventListener('pointerdown', onGesture)
      window.removeEventListener('keydown', onGesture)
    }
  }, [])

  useEffect(() => {
    const a = getAudio()
    const onEnd = () => {
      endedForRef.current = current
      playingRef.current = false
      setPlaying(false)
      setCurrent(null)
    }
    a.addEventListener('ended', onEnd)
    return () => a.removeEventListener('ended', onEnd)
  }, [getAudio, current])

  const play = useCallback(
    (sectionId) => {
      const narration = NARRATIONS[sectionId]
      if (!narration) return
      const a = getAudio()
      const token = ++tokenRef.current
      suppressAutoForRef.current = null
      endedForRef.current = null
      setListened((l) => (l.includes(sectionId) ? l : [...l, sectionId]))

      const start = () => {
        if (token !== tokenRef.current) return // a newer command superseded this one
        const wanted = window.location.origin + narration.file
        if (a.src !== wanted) a.src = narration.file
        a.currentTime = 0
        ensureGraph()
        if (audioCtxRef.current?.state === 'suspended') audioCtxRef.current.resume()
        ramp(0, 0)
        a.play()
          .then(() => {
            if (token !== tokenRef.current) return
            playingRef.current = true
            setCurrent(sectionId)
            setPlaying(true)
            ramp(1, 450)
          })
          .catch(() => {
            // browser blocked playback (no gesture yet) — stay quiet
            if (token !== tokenRef.current) return
            playingRef.current = false
            setCurrent(null)
            setPlaying(false)
          })
      }

      if (playingRef.current) {
        ramp(0, 240)
        setTimeout(() => {
          if (token !== tokenRef.current) return
          a.pause()
          start()
        }, 260)
      } else {
        start()
      }
    },
    [ensureGraph, getAudio, ramp]
  )

  const stop = useCallback(() => {
    const a = getAudio()
    tokenRef.current++ // invalidate any pending crossfade starts
    ramp(0, 90)
    a.pause()
    a.currentTime = 0
    if (current) suppressAutoForRef.current = current
    playingRef.current = false
    setPlaying(false)
    setCurrent(null)
  }, [getAudio, ramp, current])

  const canAutoPlay = useCallback(() => gestureRef.current, [])
  const autoSuppressedFor = useCallback(() => suppressAutoForRef.current, [])
  const endedFor = useCallback(() => endedForRef.current, [])

  const value = useMemo(
    () => ({
      play,
      stop,
      current,
      playing,
      getAudio,
      analyser: analyserRef,
      data: dataRef,
      canAutoPlay,
      autoSuppressedFor,
      endedFor,
      listened,
      hasListened: (id) => listened.includes(id),
    }),
    [play, stop, current, playing, getAudio, canAutoPlay, autoSuppressedFor, endedFor, listened]
  )

  return <NarrationCtx.Provider value={value}>{children}</NarrationCtx.Provider>
}

export const useNarration = () => useContext(NarrationCtx)
